import { STEPS, type Step } from './steps';
import type { AnalyticsSink } from './sink';

/**
 * Vocabulary layer — the only place event names and parameter keys are spelled. Copy, do not
 * paraphrase: a single typo silently forks the funnel from the app's.
 */
export function makeAnalytics(sink: AnalyticsSink) {
  return {
    onboardingStepReached: (s: Step) => sink.track('onboarding_step', { step: s.wire, step_index: s.page }),
    onboardingRevisited: (s: Step, from: Step) => sink.track('onboarding_revisit', { step: s.wire, from: from.wire }),
    onboardingBlocked: (s: Step, reason: string) => sink.track('onboarding_blocked', { step: s.wire, reason }),
    onboardingSkipped: (s: Step) => sink.track('onboarding_skipped', { step: s.wire }),
    onboardingCompleted: () => {
      sink.track('onboarding_completed');
      sink.flush();
    },
    identify: (id: string | null, traits?: Record<string, string>) => sink.identify(id, traits),
    reset: () => sink.reset(),
  };
}

export type Analytics = ReturnType<typeof makeAnalytics>;

/**
 * The one choke point, ported from the app's `_reportPageChange`. Every navigation inside
 * onboarding goes through here and nowhere else.
 *
 *   · `to > furthest`  → first time this far: emit the funnel step (and completion at `done`).
 *   · `to < from`      → moved backwards over covered ground: emit a revisit.
 *   · anything else    → silent. One indecisive user is still one user.
 *
 * `furthest` is injected rather than held here because the browser can reload mid-onboarding and
 * the app never had to think about that; see `furthestStore`.
 */
export function reportPageChange(
  a: Analytics,
  store: { get(): number; set(n: number): void },
  from: number,
  to: number,
): void {
  const step = STEPS[to];
  if (!step) return;
  const furthest = store.get();
  if (to > furthest) {
    store.set(to);
    a.onboardingStepReached(step);
    if (step.wire === 'done') a.onboardingCompleted();
    return;
  }
  const origin = STEPS[from];
  if (origin && to < from) a.onboardingRevisited(step, origin);
}
